import { Link, Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { PlantDetail, PlantDiagnosisRecord, PlantPhoto, PlantSummary } from "../shared/types";
import {
  createPlant,
  deletePlant,
  deleteWatering,
  fetchPlant,
  login,
  reanalyzePlant,
  updatePlant,
} from "./api";
import { useApp } from "./AppContext";
import { volumeLabel } from "./labels";
import { PlantAiNotes } from "./components/PlantAiNotes";
import { growingMediumFilterLabel } from "./plantMetadata";
import { PlantHealthPanel } from "./components/PlantHealthPanel";
import { PlantMetadataFilters } from "./components/PlantMetadataFilters";
import { PlantPhotosPanel } from "./components/PlantPhotosPanel";
import { WaterSheet } from "./components/WaterSheet";
import { matchesPlantMetadataFilters } from "./plantMetadata";

function formatShortDate(ms: number | null | undefined) {
  if (!ms) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(ms));
  } catch {
    return "—";
  }
}

function LoginModal(props: { open: boolean; onAuthed: () => void }) {
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!props.open) return null;

  return (
    <div className="sheet-backdrop" role="presentation">
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="login-title">
        <h2 id="login-title">Sign in</h2>
        <p className="muted">Enter the passcode configured for this deployment.</p>
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              await login(passcode);
              props.onAuthed();
            } catch (err) {
              setError(err instanceof Error ? err.message : "Login failed");
            } finally {
              setBusy(false);
            }
          }}
        >
          <div className="field">
            <label htmlFor="passcode">Passcode</label>
            <input
              id="passcode"
              name="passcode"
              type="password"
              autoComplete="current-password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
            />
          </div>
          {error ? <p role="alert">{error}</p> : null}
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}

function PlantCard(props: { plant: PlantSummary; onWater: (p: PlantSummary) => void }) {
  const p = props.plant;
  const next = p.schedule?.next_suggested_at;
  const overdue = next && Date.now() > next;

  return (
    <article className="plant-card">
      <Link to={`/plants/${p.id}`} className="plant-card__media" aria-label={`Open details for ${p.name}`}>
        <img src={p.photoUrl} alt="" loading="lazy" decoding="async" />
      </Link>
      <div className="plant-card__body">
        <h2 className="plant-card__title">{p.name}</h2>
        <div className="meta">
          <div className="pill" aria-live="polite">
            Next:{" "}
            <strong style={{ color: overdue ? "var(--danger)" : "var(--text)" }}>{formatShortDate(next ?? null)}</strong>
          </div>
          {p.schedule?.rationale_short ? <div style={{ marginTop: 8 }}>{p.schedule.rationale_short}</div> : null}
        </div>
        <div className="row">
          <button type="button" className="btn btn-primary" onClick={() => props.onWater(p)}>
            Water
          </button>
          <Link to={`/plants/${p.id}`} className="btn btn-ghost">
            Details
          </Link>
        </div>
      </div>
    </article>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const { plants, loading, openWaterSheet } = useApp();
  const [locationFilter, setLocationFilter] = useState<Set<string>>(() => new Set());
  const [growingFilter, setGrowingFilter] = useState<Set<string>>(() => new Set());

  const filteredPlants = useMemo(
    () => plants.filter((p) => matchesPlantMetadataFilters(p, locationFilter, growingFilter)),
    [plants, locationFilter, growingFilter],
  );

  const filtersActive = locationFilter.size > 0 || growingFilter.size > 0;

  return (
    <div>
      <div className="topbar">
        <h1>Plants</h1>
        <button type="button" className="btn btn-primary" onClick={() => navigate("/add")}>
          Add plant
        </button>
      </div>
      {loading ? <p className="muted">Loading…</p> : null}
      {!loading && plants.length === 0 ? (
        <div className="panel">
          <p style={{ margin: 0 }}>No plants yet. Add a photo to get AI care hints and start logging waterings.</p>
          <div style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-primary" onClick={() => navigate("/add")}>
              Add your first plant
            </button>
          </div>
        </div>
      ) : null}
      {!loading && plants.length > 0 ? (
        <PlantMetadataFilters
          plants={plants}
          locationFilter={locationFilter}
          onLocationFilterChange={setLocationFilter}
          growingFilter={growingFilter}
          onGrowingFilterChange={setGrowingFilter}
          filteredCount={filteredPlants.length}
        />
      ) : null}
      {!loading && plants.length > 0 && filteredPlants.length === 0 ? (
        <div className="panel">
          <p style={{ margin: 0 }}>No plants match these filters.</p>
          {filtersActive ? (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setLocationFilter(new Set());
                  setGrowingFilter(new Set());
                }}
              >
                Clear filters
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="plant-grid">
        {filteredPlants.map((p) => (
          <PlantCard key={p.id} plant={p} onWater={(plant) => openWaterSheet(plant)} />
        ))}
      </div>
    </div>
  );
}

/** Stable route component — no inline props from App (avoids remount on home list updates). */
function PlantDetailPage() {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { openWaterSheet, refreshHomePlants } = useApp();

  const [plant, setPlant] = useState<PlantDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [diagnoseBusy, setDiagnoseBusy] = useState(false);
  const [diagnosis, setDiagnosis] = useState<PlantDiagnosisRecord | null>(null);
  const [photos, setPhotos] = useState<PlantPhoto[]>([]);
  const [heroPhotoId, setHeroPhotoId] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoActionBusy, setPhotoActionBusy] = useState<string | null>(null);
  const [editEvent, setEditEvent] = useState<PlantDetail["recent_events"][number] | null>(null);
  const [deleteBusy, setDeleteBusy] = useState<string | null>(null);
  const [plantDeleteBusy, setPlantDeleteBusy] = useState(false);
  const [locationDraft, setLocationDraft] = useState("");
  const [locationSaving, setLocationSaving] = useState(false);
  const [mediumSaving, setMediumSaving] = useState(false);

  const reloadWateringOnly = useCallback(async () => {
    if (!id) return;
    const { plant: p } = await fetchPlant(id);
    setPlant((prev) =>
      prev
        ? {
            ...prev,
            schedule: p.schedule,
            last_watering: p.last_watering,
            recent_events: p.recent_events,
          }
        : p,
    );
  }, [id]);

  const reloadAiOnly = useCallback(async () => {
    if (!id) return;
    const { plant: p } = await fetchPlant(id);
    setPlant((prev) =>
      prev
        ? {
            ...prev,
            ai_profile: p.ai_profile,
            ai_profile_updated_at: p.ai_profile_updated_at,
          }
        : p,
    );
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setPageLoading(true);
    setError(null);
    void (async () => {
      try {
        const { plant: p } = await fetchPlant(id);
        if (cancelled) return;
        setPlant(p);
        setPhotos(p.photos);
        setDiagnosis(p.latest_diagnosis);
        const cover = p.photos.find((ph) => ph.is_cover) ?? p.photos[0];
        setHeroPhotoId(cover?.id ?? null);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load");
          setPlant(null);
        }
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    setLocationDraft(plant?.location ?? "");
  }, [plant?.id, plant?.location]);

  const summary = useMemo((): PlantSummary | null => {
    if (!plant) return null;
    return {
      id: plant.id,
      name: plant.name,
      location: plant.location,
      growing_medium: plant.growing_medium,
      photoUrl: plant.photoUrl,
      photo_count: plant.photo_count,
      created_at: plant.created_at,
      ai_profile: plant.ai_profile,
      ai_profile_updated_at: plant.ai_profile_updated_at,
      schedule: plant.schedule,
      last_watering: plant.last_watering,
    };
  }, [plant]);

  if (!id) return <Navigate to="/" replace />;

  if (error) {
    return (
      <div>
        <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
          Back
        </button>
        <p role="alert">{error}</p>
      </div>
    );
  }

  if (pageLoading && !plant) {
    return <p className="muted">Loading…</p>;
  }

  if (!plant) {
    return <p className="muted">Plant not found.</p>;
  }

  const ai = plant.ai_profile;
  const next = plant.schedule?.next_suggested_at;
  const heroPhoto = photos.find((ph) => ph.id === heroPhotoId) ?? photos.find((ph) => ph.is_cover) ?? photos[0];
  const heroSrc = heroPhoto?.url ?? plant.photoUrl;
  const activeDiagnosis = diagnosis ?? plant.latest_diagnosis;

  return (
    <div>
      <div className="topbar">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            void refreshHomePlants();
            navigate("/");
          }}
        >
          ← Home
        </button>
        <div className="topbar__actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => summary && openWaterSheet(summary, reloadWateringOnly)}
          >
            Water today
          </button>
          <p className="topbar__hint muted">
            Watering suggestion: <strong>{formatShortDate(next ?? null)}</strong>
          </p>
          <label className="topbar__location">
            <span className="topbar__location-label muted">Location:</span>
            <input
              type="text"
              className="topbar__location-input"
              value={locationDraft}
              placeholder="e.g. Kitchen window"
              disabled={locationSaving}
              onChange={(e) => setLocationDraft(e.target.value)}
              onBlur={async () => {
                if (!plant) return;
                const trimmed = locationDraft.trim();
                const saved = plant.location?.trim() || "";
                if (trimmed === saved) return;
                setLocationSaving(true);
                try {
                  const { location } = await updatePlant(plant.id, { location: trimmed });
                  setPlant((prev) => (prev ? { ...prev, location } : prev));
                  setLocationDraft(location ?? "");
                  void refreshHomePlants();
                } catch (err) {
                  alert(err instanceof Error ? err.message : "Failed to save location");
                  setLocationDraft(plant.location ?? "");
                } finally {
                  setLocationSaving(false);
                }
              }}
            />
            <div className="topbar__location-row">
              <span className="topbar__location-label muted">Growing:</span>
              <span className="topbar__location-value">
                {plant.growing_medium ? growingMediumFilterLabel(plant.growing_medium) : "—"}
              </span>
            </div>
          </label>
        </div>
      </div>

      <div className="hero" style={{ background: "radial-gradient(circle at 30% 20%, #2a3a55 0%, transparent 50%), #0a0f1a" }}>
        <img src={heroSrc} alt="" key={heroSrc} />
      </div>

      <div className="panel">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
          <h2 style={{ margin: 0, fontSize: "1.05rem" }}>AI notes (best guess)</h2>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={aiBusy}
            onClick={async () => {
              setAiBusy(true);
              try {
                await reanalyzePlant(plant.id);
                await reloadAiOnly();
              } finally {
                setAiBusy(false);
              }
            }}
          >
            {aiBusy ? "Re-running…" : "Re-run AI"}
          </button>
        </div>
        {aiBusy && !ai ? (
          <p className="muted" style={{ marginTop: 10 }}>
            Running AI on your photo…
          </p>
        ) : !ai ? (
          <p className="muted" style={{ marginTop: 10 }}>
            No AI notes yet. Use Re-run AI after adding a photo.
          </p>
        ) : (
          <div style={{ marginTop: 10 }}>
            <PlantAiNotes
              profile={ai}
              growingMedium={plant.growing_medium}
              growingSaving={mediumSaving}
              onGrowingMediumChange={async (next) => {
                setMediumSaving(true);
                try {
                  const { growing_medium } = await updatePlant(plant.id, { growing_medium: next });
                  setPlant((prev) => (prev ? { ...prev, growing_medium: growing_medium ?? null } : prev));
                  void refreshHomePlants();
                } catch (err) {
                  alert(err instanceof Error ? err.message : "Failed to save growing medium");
                } finally {
                  setMediumSaving(false);
                }
              }}
            />
          </div>
        )}
      </div>

      <div className="panel">
        <h2 style={{ margin: 0, fontSize: "1.05rem" }}>Watering suggestion</h2>
        <p className="muted" style={{ marginTop: 8, marginBottom: 0 }}>
          Next suggestion: <strong>{formatShortDate(next ?? null)}</strong>
        </p>
        {plant.schedule?.rationale_short ? <p style={{ marginTop: 8 }}>{plant.schedule.rationale_short}</p> : null}
      </div>

      <div className="panel">
        <h2 style={{ margin: 0, fontSize: "1.05rem" }}>Recent waterings</h2>
        <div className="timeline">
          {plant.recent_events.length === 0 ? <p className="muted">No events yet.</p> : null}
          {plant.recent_events.map((e) => (
            <div key={e.id} className="timeline-item">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                <strong>{formatShortDate(e.occurred_at)}</strong>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="muted">{volumeLabel(e.volume_bucket)}</span>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ padding: "4px 10px", fontSize: "0.85rem" }}
                    onClick={() => setEditEvent(e)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-danger"
                    style={{ padding: "4px 10px", fontSize: "0.85rem" }}
                    disabled={deleteBusy === e.id}
                    onClick={async () => {
                      if (!confirm("Delete this watering log?")) return;
                      setDeleteBusy(e.id);
                      try {
                        await deleteWatering(plant.id, e.id);
                        if (editEvent?.id === e.id) setEditEvent(null);
                        await reloadWateringOnly();
                        await refreshHomePlants();
                      } catch (err) {
                        alert(err instanceof Error ? err.message : "Failed to delete");
                      } finally {
                        setDeleteBusy(null);
                      }
                    }}
                  >
                    {deleteBusy === e.id ? "…" : "Delete"}
                  </button>
                </div>
              </div>
              <div style={{ marginTop: 6 }}>{e.method_text}</div>
            </div>
          ))}
        </div>
      </div>

      <PlantPhotosPanel
        plantId={plant.id}
        photos={photos}
        heroPhotoId={heroPhotoId}
        photoBusy={photoBusy}
        photoActionBusy={photoActionBusy}
        onHeroPhotoId={setHeroPhotoId}
        onPhotosChange={(p) => {
          setPlant(p);
          setPhotos(p.photos);
        }}
        onPhotoBusy={setPhotoBusy}
        onPhotoActionBusy={setPhotoActionBusy}
        onRefreshHome={refreshHomePlants}
      />

      <PlantHealthPanel
        plantId={plant.id}
        photos={photos}
        diagnosis={activeDiagnosis}
        diagnoseBusy={diagnoseBusy}
        onDiagnose={setDiagnosis}
        onDiagnoseBusy={setDiagnoseBusy}
      />

      <div className="panel" style={{ borderColor: "rgba(251, 113, 133, 0.35)" }}>
        <h2 style={{ margin: 0, fontSize: "1.05rem" }}>Remove plant</h2>
        <p className="muted" style={{ marginTop: 8, marginBottom: 12 }}>
          Permanently deletes {plant.name}, all photos, and watering history.
        </p>
        <button
          type="button"
          className="btn btn-danger"
          disabled={plantDeleteBusy}
          onClick={async () => {
            if (
              !confirm(
                `Delete "${plant.name}" and all of its watering history? This cannot be undone.`,
              )
            ) {
              return;
            }
            setPlantDeleteBusy(true);
            try {
              await deletePlant(plant.id);
              await refreshHomePlants();
              navigate("/");
            } catch (err) {
              alert(err instanceof Error ? err.message : "Failed to delete plant");
            } finally {
              setPlantDeleteBusy(false);
            }
          }}
        >
          {plantDeleteBusy ? "Deleting…" : "Delete plant"}
        </button>
      </div>

      {summary && editEvent ? (
        <WaterSheet
          plant={summary}
          editEvent={editEvent}
          onClose={() => setEditEvent(null)}
          onLogged={async () => {
            await reloadWateringOnly();
            await refreshHomePlants();
            setEditEvent(null);
          }}
        />
      ) : null}
    </div>
  );
}

function AddPlantPage() {
  const navigate = useNavigate();
  const { refreshHomePlants } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <div>
      <div className="topbar">
        <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
          Cancel
        </button>
        <h1 style={{ margin: 0 }}>Add plant</h1>
        <span />
      </div>

      <div className="panel">
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!file) {
              setError("Choose a photo");
              return;
            }
            setBusy(true);
            setError(null);
            try {
              const fd = new FormData();
              fd.set("photo", file);
              const { id } = await createPlant(fd);
              await refreshHomePlants();
              navigate(`/plants/${id}`);
            } catch (err) {
              setError(err instanceof Error ? err.message : "Failed");
            } finally {
              setBusy(false);
            }
          }}
        >
          <div className="field">
            <label htmlFor="pphoto">Photo</label>
            <input
              id="pphoto"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          {error ? <p role="alert">{error}</p> : null}
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save plant"}
          </button>
        </form>
      </div>
    </div>
  );
}

export function App() {
  const { needLogin, setNeedLogin, waterSheetPlant, closeWaterSheet, onWaterSaved, refreshHomePlants } = useApp();

  return (
    <div className="app-shell">
      <LoginModal
        open={needLogin}
        onAuthed={() => {
          setNeedLogin(false);
          void refreshHomePlants();
        }}
      />

      {waterSheetPlant ? (
        <WaterSheet plant={waterSheetPlant} onClose={closeWaterSheet} onLogged={onWaterSaved} />
      ) : null}

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/plants/:id" element={<PlantDetailPage />} />
        <Route path="/add" element={<AddPlantPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
